import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Send, Loader2, Sparkles, Bot } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = "sm_ema_conv";

const ICON_MAP = {
  "help-circle": "?", "info": "€", "plus-circle": "+",
  "bell": "🔔", "list": "≡",
};

function formatText(t) {
  if (!t) return "";
  return t
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br />");
}

/**
 * Ema — plovoucí AI chatbot bubble (bottom-right).
 * Skrytý na /os/* (interný portál) — zobrazuje sa iba na verejnom webe.
 */
export default function ChatbotWidget() {
  const location = useLocation();
  const isInternal = location.pathname.startsWith("/os")
    || location.pathname.startsWith("/admin");
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [quickActions, setQuickActions] = useState([]);
  const [greetShown, setGreetShown] = useState(false);
  const bottomRef = useRef(null);

  // Restore conversation from localStorage.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data.conversation_id) setConversationId(data.conversation_id);
        if (data.messages) setMessages(data.messages);
      }
    } catch { /* noop */ }
  }, []);

  // Persist.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        conversation_id: conversationId, messages,
      }));
    } catch { /* noop */ }
  }, [conversationId, messages]);

  // Load quick actions.
  useEffect(() => {
    if (isInternal) return;
    axios.get(`${API}/chatbot/quick-actions`)
      .then((r) => setQuickActions(r.data?.actions || []))
      .catch(() => setQuickActions([]));
  }, [isInternal]);

  // Auto-scroll on new msg.
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  // Show greeting first time widget opens.
  useEffect(() => {
    if (open && !greetShown && messages.length === 0) {
      setMessages([{
        role: "assistant",
        text: "**Dobrý deň! Som Ema** 👋\n\nSom AI asistentka SuperMajstri. Rada vám pomôžem s výberom vhodnej služby — rekonštrukcie, zateplenia, novostavby, bazény. Aj s dohodnutím bezplatnej obhliadky.\n\nS čím môžem pomôcť?",
      }]);
      setGreetShown(true);
    }
  }, [open, greetShown, messages.length]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setSending(true);
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    try {
      const r = await axios.post(`${API}/chatbot/message`, {
        message:         msg,
        conversation_id: conversationId,
        language:        "sk",
        page_url:        window.location.pathname,
      });
      setConversationId(r.data.conversation_id);
      setMessages((prev) => [...prev, {
        role: "assistant", text: r.data.text,
        confidence: r.data.confidence,
      }]);
    } catch (e) {
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: "Prepáčte, momentálne mám technické ťažkosti. Napíšte nám prosím na info@supermajstri.sk alebo +421 907 649 964.",
        confidence: 0,
      }]);
    } finally {
      setSending(false);
    }
  }, [input, sending, conversationId]);

  const reset = () => {
    setMessages([]);
    setConversationId(null);
    setGreetShown(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  };

  if (isInternal) return null;

  return (
    <>
      {/* Floating action button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[9998] w-16 h-16 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#0088ff] text-black shadow-2xl hover:scale-105 transition-transform flex items-center justify-center"
          data-testid="ema-fab"
          aria-label="Otvoriť chat s Emou"
        >
          <Sparkles size={26} strokeWidth={2.5} />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#FF5E00] border-2 border-black animate-pulse" />
        </button>
      )}

      {/* Widget window */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-[9999] w-[calc(100vw-3rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-3rem)] rounded-3xl border border-[#00E5FF]/30 bg-black shadow-2xl flex flex-col overflow-hidden"
          data-testid="ema-widget"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#00E5FF] to-[#0088ff] p-4 flex items-center justify-between text-black">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                <Bot size={18} className="text-[#00E5FF]" />
              </div>
              <div>
                <div className="font-black text-sm">Ema</div>
                <div className="text-[10px] mono uppercase tracking-widest opacity-70">
                  AI asistentka · online
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={reset}
                className="text-[10px] mono uppercase tracking-widest px-2 py-1 rounded hover:bg-black/10"
                data-testid="ema-reset"
                aria-label="Nová konverzácia">
                Nová
              </button>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-black/10 flex items-center justify-center"
                data-testid="ema-close"
                aria-label="Zavrieť chat">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-black">
            {messages.map((m, i) => (
              <div key={i}
                className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`ema-msg-${m.role}`}>
                {m.role === "assistant" && (
                  <div className="shrink-0 w-7 h-7 rounded-full bg-[#00E5FF]/20 border border-[#00E5FF]/40 flex items-center justify-center">
                    <Bot size={12} className="text-[#00E5FF]" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed
                  ${m.role === "user"
                    ? "bg-[#00E5FF] text-black rounded-br-sm"
                    : "bg-white/[0.05] text-zinc-200 border border-white/10 rounded-bl-sm"}`}>
                  <div dangerouslySetInnerHTML={{ __html: formatText(m.text) }} />
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-2">
                <div className="shrink-0 w-7 h-7 rounded-full bg-[#00E5FF]/20 border border-[#00E5FF]/40 flex items-center justify-center">
                  <Bot size={12} className="text-[#00E5FF]" />
                </div>
                <div className="rounded-2xl px-3 py-2 bg-white/[0.05] border border-white/10 text-[11px] text-zinc-400 flex items-center gap-1">
                  <Loader2 size={12} className="animate-spin" /> Ema píše…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick actions */}
          {messages.length <= 1 && quickActions.length > 0 && !sending && (
            <div className="px-3 pb-2 flex flex-wrap gap-1 bg-black">
              {quickActions.slice(0, 4).map((qa) => (
                <button key={qa.id} onClick={() => send(qa.prompt)}
                  className="text-[11px] px-2 py-1 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 hover:border-[#00E5FF]/40"
                  data-testid={`ema-quick-${qa.id}`}>
                  {ICON_MAP[qa.icon] || "•"} {qa.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-white/10 p-3 flex gap-2 bg-black">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Napíšte správu…" disabled={sending}
              className="flex-1 bg-white/[0.05] border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#00E5FF]/60"
              data-testid="ema-input"
              autoComplete="off" />
            <button type="submit" disabled={sending || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#00E5FF] text-black flex items-center justify-center disabled:opacity-40 hover:bg-[#33EEFF]"
              data-testid="ema-send"
              aria-label="Odoslať">
              <Send size={14} />
            </button>
          </form>
          <div className="text-center text-[10px] text-zinc-600 pb-2 bg-black">
            AI odpovede môžu byť nepresné. Overte kontaktovaním SuperMajstri.
          </div>
        </div>
      )}
    </>
  );
}
